import React, { useState, useCallback, useRef, useLayoutEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Can } from '@iq/react-components';
import ExportTimeSeriesModal from '../SiteAdminView/VariableView/components/ExportTimeSeriesModal';
import FullscreenModal from './visualizations/FullscreenModal';
import { getSiteVariables, getRawSourceConnectivityById } from '../../bundles/sources';
import { getStateSets } from '../../bundles/statesets';
import { LAST_VALUE_VISUALIZATIONS } from '../../bundles/visualizations/actions';
import { getLastVariableData } from '../../bundles/variables';
import { useVariableSubscription } from '../../utils';
import { getStarPlotVariables } from './visualizations/timeSeries/StarVisualization/helpers';

import GraphVisualization, {
  getSchema as graphSchema,
  uiSchema as graphUiSchema,
} from './visualizations/timeSeries/GraphVisualization';
import GroupedBarVisualization, {
  getSchema as groupedBarSchema,
  uiSchema as groupedBarUiSchema,
} from './visualizations/lastValue/GroupedBarVisualization';
import MetricVisualization, {
  getSchema as metricSchema,
  uiSchema as metricUiSchema,
} from './visualizations/lastValue/MetricVisualization';
import StatusVisualization, {
  getSchema as statusSchema,
  uiSchema as statusUiSchema,
} from './visualizations/lastValue/StatusVisualization';
import BarGaugeVisualization, {
  getSchema as barGaugeSchema,
  uiSchema as barGaugeUiSchema,
} from './visualizations/lastValue/BarGaugeVisualization';
import GaugeVisualization, {
  getSchema as gaugeSchema,
  uiSchema as gaugeUiSchema,
} from './visualizations/lastValue/GaugeVisualization';
import StateVisualization, {
  getSchema as stateSchema,
  uiSchema as stateUiSchema,
} from './visualizations/lastValue/StateVisualization';
import PieVisualization, {
  getSchema as pieSchema,
  uiSchema as pieUiSchema,
} from './visualizations/lastValue/PieVisualization';
import StarVisualization, {
  getSchema as starSchema,
  uiSchema as starUiSchema,
} from './visualizations/timeSeries/StarVisualization';
import DuvalVisualization, {
  getSchema as duvalSchema,
  uiSchema as duvalUiSchema,
} from './visualizations/timeSeries/DuvalVisualization';
import DuvalPentagonVisualization, {
  getSchema as duvalPentagonSchema,
  uiSchema as duvalPentagonUiSchema,
} from './visualizations/timeSeries/DuvalPentagonVisualization';
import Tooltip from '../Tooltip/Tooltip';
import { buildVisualizationVariables } from './buildVisualizationVariables';

const visualizations = [
  {
    name: 'Graph',
    type: 'graph',
    category: 'graphs',
    icon: 'he-line-chart-alt',
    minSignal: 1,
    maxSignal: 10,
    component: GraphVisualization,
    schema: graphSchema,
    uiSchema: graphUiSchema,
    prioritizedSignalsLabel: 'Graph series',
  },
  {
    name: 'Bar group',
    type: 'grouped-bar',
    category: 'graphs',
    icon: 'he-barchart',
    minSignal: 1,
    maxSignal: 24,
    component: GroupedBarVisualization,
    schema: groupedBarSchema,
    uiSchema: groupedBarUiSchema,
  },
  {
    name: 'Metric',
    type: 'metric',
    category: 'metrics',
    icon: 'he-numericals',
    minSignal: 1,
    component: MetricVisualization,
    schema: metricSchema,
    uiSchema: metricUiSchema,
  },
  {
    name: 'Status',
    type: 'status',
    category: 'metrics',
    icon: 'he-status',
    minSignal: 1,
    component: StatusVisualization,
    schema: statusSchema,
    uiSchema: statusUiSchema,
  },
  {
    name: 'Bar gauge',
    type: 'bar-gauge',
    category: 'graphs',
    icon: 'he-barchart',
    minSignal: 1,
    maxSignal: 10,
    component: BarGaugeVisualization,
    schema: barGaugeSchema,
    uiSchema: barGaugeUiSchema,
    prioritizedSignalsLabel: 'the Single Signal configuration',
  },
  {
    name: 'Gauge',
    type: 'gauge',
    category: 'graphs',
    icon: 'he-speedometer',
    minSignal: 1,
    component: GaugeVisualization,
    schema: gaugeSchema,
    uiSchema: gaugeUiSchema,
  },
  {
    name: 'State',
    type: 'state',
    category: 'State',
    icon: 'he-states',
    minSignal: 1,
    component: StateVisualization,
    schema: stateSchema,
    uiSchema: stateUiSchema,
  },
  {
    name: 'Pie',
    type: 'pie',
    category: 'Pie',
    icon: 'he-piechart',
    minSignal: 1,
    maxSignal: 10,
    component: PieVisualization,
    schema: pieSchema,
    uiSchema: pieUiSchema,
  },
  {
    name: 'Star',
    type: 'star',
    category: 'Star',
    icon: 'he-favoritefill',
    minSignal: 2,
    maxSignal: 3,
    component: StarVisualization,
    schema: starSchema,
    uiSchema: starUiSchema,
    prioritizedSignalsLabel: 'the Star plot',
  },
  {
    name: 'Duval',
    subname: 'Triangle',
    type: 'duval',
    category: 'duval',
    icon: 'he-triangle',
    minSignal: 3,
    maxSignal: 9,
    component: DuvalVisualization,
    schema: duvalSchema,
    uiSchema: duvalUiSchema,
    prioritizedSignalsLabel: 'Duval Triangle plots',
  },
  {
    name: 'Duval',
    subname: 'Pentagon',
    type: 'duvalPentagon',
    category: 'duval',
    icon: 'he-pentagon',
    minSignal: 5,
    maxSignal: 15,
    component: DuvalPentagonVisualization,
    schema: duvalPentagonSchema,
    uiSchema: duvalPentagonUiSchema,
    prioritizedSignalsLabel: 'Duval Pentagon plots',
  },
];

const config = (type) =>
  visualizations.find(({ type: _type }) => _type === type) || {
    schema: {},
    component: () => 'No component',
  };

const schema = (type) => config(type).schema;

const uiSchema = (type) => config(type).uiSchema;

const component = (type) => config(type).component;

const signalCounts = (type) => {
  const { minSignal, maxSignal } = config(type);
  return {
    minSignal: minSignal || 1,
    maxSignal: maxSignal || minSignal,
  };
};

const Visualization = ({ visualization, exportGranularity, isPreview, ...properties }) => {
  const siteVariables = useSelector(getSiteVariables);
  const stateSets = useSelector(getStateSets);
  const sourceConnectivity = useSelector(getRawSourceConnectivityById);
  const lastVariableData = useSelector(getLastVariableData);

  const variables = useMemo(
    () => buildVisualizationVariables(visualization.variables, siteVariables, stateSets),
    [siteVariables, stateSets, visualization.variables]
  );

  const isLastValue = LAST_VALUE_VISUALIZATIONS.has(visualization.type);
  const variableIds = useMemo(
    () => (isLastValue ? variables.map((v) => v.id) : []),
    [isLastValue, variables]
  );

  useVariableSubscription(properties.panelId, variableIds);

  const latestValues = useMemo(() => {
    if (!isLastValue) return;
    if (!lastVariableData || !variableIds.every((id) => id in lastVariableData)) {
      return null;
    }
    return variables.map((v) => ({
      variable: v.id,
      value: lastVariableData[v.id] || [],
    }));
  }, [isLastValue, lastVariableData, variableIds, variables]);

  /*
   * Determine if any of the sources for the current visualization are disconnected.
   * The `disconnected` prop used exclusively in last-value type visualizations
   * lets the component know that at least one of its signals' sources are currently disconnected.
   * This safeguards against receiving empty data sets that do not include quality info.
   */
  let disconnected = false;
  for (const { source } of variables) {
    const sourceStatus = source ? sourceConnectivity[source.id] : undefined;
    if (sourceStatus === 0) {
      disconnected = true;
      break;
    }
  }

  const currentConfig = useMemo(() => config(visualization.type), [visualization.type]);
  const Component = currentConfig.component;
  const currentSignalCounts = useMemo(
    () => ({
      minSignal: currentConfig.minSignal || 1,
      maxSignal: currentConfig.maxSignal || currentConfig.minSignal,
    }),
    [currentConfig]
  );
  const visualizationName = `${currentConfig.name ?? ''} ${currentConfig.subname ?? ''}`.trim();
  const visualizationWithVariables = useMemo(
    () => ({ ...visualization, variables }),
    [visualization, variables]
  );
  const coreVisualizationReference = useRef();
  const [showExportForm, setShowExportForm] = useState(false);

  const [showFullscreen, setShowFullscreen] = useState(false);
  const [dropdownPlacement, setDropdownPlacement] = useState({ x: 'right', y: 'bottom' });

  const handleCloseExportModal = useCallback(() => setShowExportForm(false), [setShowExportForm]);

  const handleExportData = useCallback(() => {
    setShowExportForm(true);
  }, []);

  const handleFullscreen = useCallback(() => {
    setShowFullscreen(true);
  }, []);

  const handleCloseFullscreenModal = useCallback(() => {
    setShowFullscreen(false);
  }, []);

  // Since the duval and star point don't display all signals,
  // we need to extract the signals that are actually assigned to plots
  const findExportVariable = (signal) => {
    if (!signal) return;

    const [id, aggregate] = signal.split('.');

    return (
      variables.find((variable) => variable.id === id && variable.aggregate === aggregate) ||
      variables.find((variable) => variable.id === id)
    );
  };

  const exportVariables = () => {
    switch (visualization.type) {
      case 'duval': {
        return (visualization.configuration.plots || [])
          .flatMap((plot) => [plot.leftId, plot.rightId, plot.bottomId])
          .map((signal) => findExportVariable(signal))
          .filter(Boolean);
      }
      case 'duvalPentagon': {
        return (visualization.configuration.plots || [])
          .flatMap((plot) => [plot.h2Id, plot.c2h6Id, plot.ch4Id, plot.c2h4Id, plot.c2h2Id])
          .map((signal) => findExportVariable(signal))
          .filter(Boolean);
      }
      case 'star': {
        return getStarPlotVariables({ configuration: visualization.configuration, variables });
      }
      default: {
        return variables;
      }
    }
  };

  const getMoreOptions = ({ site = {}, onFullscreen, handleDownloadClick, openSignalViewer }) => {
    const options = [];

    if (handleDownloadClick) {
      options.push({
        component: (
          <Can
            permission="variables/Read"
            scope={{ org: site.org, site: site.id }}
          >
            <button type="button">Download</button>
          </Can>
        ),
        onSelect: () => handleDownloadClick(),
      });
    }

    if (openSignalViewer) {
      options.push({
        component: <button type="button">Open in Signal Viewer</button>,
        onSelect: () => openSignalViewer(visualization),
      });
    }

    if (onFullscreen) {
      options.push({
        component: <button type="button">Fullscreen</button>,
        onSelect: () => onFullscreen(),
      });
    }

    return options;
  };

  const updateMenuPlacement = useCallback(() => {
    if (coreVisualizationReference.current) {
      const panelElement = coreVisualizationReference.current?.parentElement?.parentElement;
      if (panelElement) {
        const { right: panelElementRight } = panelElement.getBoundingClientRect();
        const childRect = coreVisualizationReference?.current.getBoundingClientRect();
        const spaceRight = panelElementRight - childRect.right;
        setDropdownPlacement({ x: spaceRight > 150 ? 'right' : 'left', y: 'bottom' });
      }
    }
  }, [coreVisualizationReference]);

  useLayoutEffect(() => {
    const resizeObserver = new ResizeObserver(updateMenuPlacement);
    const currentReference = coreVisualizationReference.current;
    if (currentReference) {
      resizeObserver.observe(currentReference);
    }

    return () => {
      if (currentReference) {
        resizeObserver.unobserve(currentReference);
      }
      resizeObserver.disconnect();
    };
  }, [coreVisualizationReference, updateMenuPlacement]);

  return (
    <>
      <div
        className="corevisualization-component"
        ref={coreVisualizationReference}
      >
        <Component
          visualization={visualizationWithVariables}
          signalCounts={currentSignalCounts}
          visualizationName={visualizationName}
          onExportData={handleExportData}
          onFullscreen={handleFullscreen}
          CustomTooltip={Tooltip}
          getMoreOptions={getMoreOptions}
          dropdownPlacement={dropdownPlacement}
          disconnected={disconnected}
          isPreview={isPreview}
          latestValues={latestValues}
          initialLoaded={isLastValue ? Boolean(latestValues) : undefined}
          {...properties}
        />
      </div>
      {showExportForm && visualization.variables?.length > 0 && (
        <ExportTimeSeriesModal
          selectedVizVariables={exportVariables()}
          pollingDateRange={properties.pollingDateRange}
          onCloseModal={handleCloseExportModal}
          showAggregation={false}
          exportGranularity={exportGranularity}
          onExportCompleted={handleCloseExportModal}
          parentPanelId={properties.panelId}
        />
      )}
      {showFullscreen && (
        <FullscreenModal
          visualization={visualization}
          handleExportData={handleExportData}
          onSelect={properties.onSelect}
          openSignalViewer={properties.openSignalViewer}
          onClose={handleCloseFullscreenModal}
          Component={
            <Component
              visualization={visualizationWithVariables}
              signalCounts={currentSignalCounts}
              visualizationName={visualizationName}
              onExportData={handleExportData}
              onFullscreen={handleFullscreen}
              isFullscreen={showFullscreen}
              disconnected={disconnected}
              latestValues={latestValues}
              initialLoaded={isLastValue ? Boolean(latestValues) : undefined}
              {...properties}
            />
          }
        />
      )}
    </>
  );
};

export default Visualization;
export { schema, uiSchema, config, component, signalCounts, visualizations };
export {
  getRequiredStarSignalCount,
  StarSignalCountMessage,
} from './visualizations/timeSeries/StarVisualization';
